import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { client, unwrap } from "@/api/client";
import type { components } from "@/api/schema";
import { t } from "@/i18n";

export type Lead = components["schemas"]["Lead"];
export type LeadChannel = NonNullable<Lead["channel"]>;
export type TriageResult = components["schemas"]["TriageResult"];
export type Agent = components["schemas"]["Agent"];

/** Which slice of the intake queue is on screen. */
export type QueueFilter = "pending" | "done" | "all";

/**
 * Every channel the legacy `Recepcion` table recorded.
 *
 * The integers are legacy values and must never be renumbered — same rule as
 * the company statuses. `6` is Papel, which nothing creates any more but the
 * imported rows still carry.
 */
export const CHANNELS: readonly LeadChannel[] = [1, 2, 3, 4, 5, 6];

export function channelFromParam(raw: string | null): string {
  if (!raw) return "";
  return CHANNELS.includes(Number(raw) as LeadChannel) ? raw : "";
}

export function queueFilterFromParam(raw: string | null): QueueFilter {
  return raw === "done" || raw === "all" ? raw : "pending";
}

export type LeadFilters = {
  queue: QueueFilter;
  /** Empty string means "any". A legacy channel integer as text. */
  channel: string;
  search: string;
  page: number;
};

export function useLeads(filters: LeadFilters) {
  return useQuery({
    queryKey: ["leads", filters],
    queryFn: () =>
      unwrap(
        client.GET("/api/leads/", {
          params: {
            query: {
              page: filters.page,
              // Newest first: the call reception just took sits at the top,
              // where they look for it, instead of on the last page.
              ordering: "-created",
              // An untriaged lead is one with no agent (F1). This is the queue.
              ...(filters.queue === "pending" ? { current_agent__isnull: true } : {}),
              ...(filters.queue === "done" ? { current_agent__isnull: false } : {}),
              ...(filters.search ? { search: filters.search } : {}),
              ...(filters.channel
                ? { channel: Number(filters.channel) as LeadChannel }
                : {}),
            },
          },
        }),
        t("errors.leads"),
      ),
    placeholderData: keepPreviousData,
  });
}

/**
 * Who a lead can be handed to.
 *
 * The one endpoint where a signed-in user sees other people, and it 403s for
 * anyone who cannot assign — so this is deliberately only called from screens
 * already gated on the same roles.
 */
export function useAgents(enabled = true) {
  return useQuery({
    queryKey: ["agents"],
    enabled,
    queryFn: () => unwrap(client.GET("/api/users/agents/"), t("errors.agents")),
    // Staff changes are an admin action, not something that happens while a
    // queue is open.
    staleTime: 5 * 60_000,
  });
}

/**
 * Assign the lead, and create whatever it still needs.
 *
 * One request, one `transaction.atomic()`, both branches (D13): if the lead
 * already points at a company only an `Activity` is written; if it does not,
 * the whole `Company` → `Branch` → `Contact` → `ContactPhone` chain is created
 * and owned by the agent. The SPA does not get to choose — offering the two as
 * separate buttons would put a multi-table invariant in the browser.
 */
export function useTriage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, agent }: { id: number; agent: number }): Promise<TriageResult> =>
      unwrap(
        client.POST("/api/leads/{id}/triage/", {
          params: { path: { id } },
          body: { agent },
        }),
        t("errors.triage"),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["leads"] });
      // A company may have just come into existence, and an `Activity`
      // definitely did.
      void queryClient.invalidateQueries({ queryKey: ["companies"] });
      void queryClient.invalidateQueries({ queryKey: ["activities"] });
      // An unknown WhatsApp sender that had a lead behind it is now linked.
      void queryClient.invalidateQueries({ queryKey: ["conversations"] });
    },
  });
}

/**
 * The acquisition-source flags, as one readable list.
 *
 * The legacy table records these as eight booleans rather than a choice, and
 * more than one can be set — someone can arrive from a map *and* a referral.
 * Collapsing them to a single value would lose that.
 */
const SOURCE_FLAGS = [
  "from_newspaper",
  "from_email",
  "from_newsletter",
  "from_map",
  "from_search",
  "from_referral",
  "from_visit",
  "from_social",
] as const satisfies readonly (keyof Lead)[];

export function sourcesOf(lead: Lead): string[] {
  const found = SOURCE_FLAGS.filter((flag) => lead[flag] === true).map((flag) =>
    t(`leads.sources.${flag}`),
  );
  // Free text the person keyed in, so it is shown as written rather than
  // looked up.
  if (lead.other_source) found.push(lead.other_source);
  return found;
}
